import { StrictMode } from 'react';
import { loadSounds } from '@/lib/sound';
import { trackViewport } from '@/lib/viewport';
import { trackAwake } from '@/lib/awake';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { DetachedDock } from '@/components/DetachedDock';
import { useSession, wireSession } from './stores/session';
import { wireVoice } from './stores/voice';
import './styles/index.css';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * Coming back to the window checks whatever has gone stale.
       *
       * This was off because the socket pushes changes - and it does, for the
       * things it knows about. It does not know about a profile you opened in
       * one place and acted on in another, or anything changed from a second
       * window, or whatever happened while the laptop was shut. This is not
       * polling: staleTime still applies, so anything fetched in the last
       * fifteen seconds is left alone and coming back to a quiet app asks for
       * nothing at all.
       */
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 15_000,
      /*
       * Answers for screens nobody is looking at are dropped after two
       * minutes rather than the default five. An evening of moving between
       * parties, profiles and chats otherwise leaves the whole lot in memory;
       * coming back fetches again, which the socket has usually made stale
       * anyway.
       */
      gcTime: 120_000
    }
  }
});

/*
 * ?dock=1 means this window is the detached voice dock rather than the app.
 *
 * It renders one component and nothing else - no session, no socket, no
 * queries. The app window owns the call and pushes state across, so there is
 * only ever one client connected however many windows are open.
 */
trackViewport();
trackAwake();

const isDock = new URLSearchParams(location.search).has('dock');

if (isDock) {
  document.documentElement.dataset.theme =
    localStorage.getItem('pogu.theme') ?? 'midnight';
  const dockAccent = localStorage.getItem('pogu.accent');
  if (dockAccent && dockAccent !== 'violet') document.documentElement.dataset.accent = dockAccent;
  document.body.style.background = 'transparent';
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <DetachedDock />
    </StrictMode>
  );
} else {
  wireSession();
  wireVoice();
  void useSession.getState().boot();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>
  );
}

// Browsers refuse to start an AudioContext before a gesture, so the clips are
// decoded on the first one rather than at boot - after which every cue is
// instant. Once is enough.
window.addEventListener('pointerdown', () => void loadSounds(), { once: true });
window.addEventListener('keydown', () => void loadSounds(), { once: true });
